import { createContext, useContext, useEffect, useState, type ReactNode } from "react";

import { useAuth } from "@/contexts/AuthContext";
import { useOrgs } from "@/hooks/queries/useOrgs";
import type { Organization } from "@/lib/api";

const CURRENT_ORG_STORAGE_KEY = "vadium.currentOrgId";

export interface OrgContextValue {
  /** All organizations the signed-in user belongs to. */
  orgs: Organization[];
  orgsLoading: boolean;
  /** The active organization (first org used as default when nothing is stored). */
  currentOrg: Organization | null;
  currentOrgId: string | null;
  /** Switch the active organization. */
  setCurrentOrg: (orgId: string) => void;
}

const OrgContext = createContext<OrgContextValue | undefined>(undefined);

export function OrgProvider({ children }: { children: ReactNode }) {
  const { isAuthenticated, authLoading } = useAuth();
  const { data: orgs = [], isLoading: orgsLoading, isSuccess: orgsLoaded, isFetching: orgsFetching } = useOrgs({
    enabled: !authLoading && isAuthenticated,
  });
  const [currentOrgId, setCurrentOrgId] = useState<string | null>(null);

  useEffect(() => {
    const stored = typeof window !== "undefined" ? window.localStorage.getItem(CURRENT_ORG_STORAGE_KEY) : null;
    if (stored) {
      setCurrentOrgId(stored);
    }
  }, []);

  // Auto-select the first org when nothing is stored or the stored selection is
  // stale (e.g. membership revoked). Only reconcile against a settled list:
  // while the query is disabled/loading or refetching after an invalidation the
  // list is not authoritative — clearing/changing the selection here would be
  // spurious.
  useEffect(() => {
    if (!orgsLoaded || orgsFetching) {
      return;
    }
    if (currentOrgId !== null && orgs.some((org) => org.id === currentOrgId)) {
      return;
    }
    const fallback = orgs[0]?.id ?? null;
    setCurrentOrgId(fallback);
    if (typeof window !== "undefined") {
      if (fallback) {
        window.localStorage.setItem(CURRENT_ORG_STORAGE_KEY, fallback);
      } else {
        window.localStorage.removeItem(CURRENT_ORG_STORAGE_KEY);
      }
    }
  }, [orgs, orgsLoaded, orgsFetching, currentOrgId]);

  const setCurrentOrg = (orgId: string) => {
    setCurrentOrgId(orgId);
    if (typeof window !== "undefined") {
      window.localStorage.setItem(CURRENT_ORG_STORAGE_KEY, orgId);
    }
  };

  const currentOrg = orgs.find((org) => org.id === currentOrgId) ?? null;

  const value: OrgContextValue = { orgs, orgsLoading, currentOrg, currentOrgId, setCurrentOrg };

  return <OrgContext.Provider value={value}>{children}</OrgContext.Provider>;
}

export function useCurrentOrg() {
  const value = useContext(OrgContext);
  if (!value) {
    throw new Error("useCurrentOrg must be used within OrgProvider");
  }

  return value;
}
